import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import CustomModal from "../../packages/CustomModal";
import WPAgGrid from "../../packages/WPAgGrid/WPAgGrid";
import { useToastNotification } from "../../packages/WPToastNotification";

import DeleteRenderer from "../../components/AddCIPAndShutdown/deleteCellRender";
import DupontButton from "../../components/DupontButton";
import InputBox from "../../components/InputBox";

import { calculateTableHeight } from "../../utils/tableDataFormatter";
import { getIndex } from "../../utils/Utils";

import {
  getPlants,
  getUsers,
  sharePlant,
  unsharePlant,
} from "../../network/cipAPI";

import { CustomModalButtons } from "../../constants/global.constants";

import { storePlantRowData } from "../../Store/TemplateStore/template.action";

export const CustomMembraneCellRenderer = ({
  data: { sharing, is_owner, plant_id },
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [rowData, setRowData] = useState([]);
  const [comments, setComments] = useState("");
  const [showOverlayLoader, setShowOverlayLoader] = useState(false);
  const usersRequestIdRef = useRef(0);
  const isPlantsRefreshInFlightRef = useRef(false);

  const dispatch = useDispatch();
  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();

  const refreshPlants = async () => {
    if (isPlantsRefreshInFlightRef.current) {
      return;
    }

    isPlantsRefreshInFlightRef.current = true;
    try {
      const response = await getPlants();
      if (response && response.data) {
        dispatch(storePlantRowData(response.data));
      } else {
        dispatch(storePlantRowData([]));
      }
    } catch (err) {
    } finally {
      isPlantsRefreshInFlightRef.current = false;
    }
  };

  const handleConfirm = async () => {
    setShowOverlayLoader(true);
    try {
      const payload = { shared_email: email, comments };

      const response = await sharePlant(plant_id, payload);
      if (response && response.message) {
        setDialogOpen(false);
        showSuccessNotification(response.message);
        refreshPlants();
      } else {
        showErrorNotification(response.detail.error);
        setErrorMsg(response.detail.error);
      }
    } catch (error) {
      showErrorNotification(t("notificationStrings.somethingWrong"));
    } finally {
      setShowOverlayLoader(false);
    }
  };

  const getUserInfo = async () => {
    usersRequestIdRef.current += 1;
    const requestId = usersRequestIdRef.current;

    setShowOverlayLoader(true);
    try {
      const response = await getUsers(plant_id);
      if (requestId !== usersRequestIdRef.current) {
        return;
      }

      if (response) {
        setRowData(response.data || []);
      }
    } catch (error) {
      if (requestId !== usersRequestIdRef.current) {
        return;
      }

      showErrorNotification(t("notificationStrings.somethingWrong"));
    } finally {
      if (requestId === usersRequestIdRef.current) {
        setShowOverlayLoader(false);
      }
    }
  };

  const handleCancel = () => {
    usersRequestIdRef.current += 1;
    setDialogOpen(false);
    setErrorMsg("");
    setRowData([]);
    refreshPlants();
  };

  const onClose = ({ target } = {}) => {
    if (target?.id === CustomModalButtons.CONFIRM) {
      handleConfirm();
    } else if (
      target?.id === CustomModalButtons.CANCEL ||
      target?.id === CustomModalButtons.CLOSE
    ) {
      handleCancel();
    }
  };

  const handleUnshare = async (email) => {
    setShowOverlayLoader(true);
    try {
      const response = await unsharePlant(plant_id, email);
      if (response && response.message) {
        setRowData((prev) => prev.filter((user) => user.email !== email));
        showSuccessNotification(response.message);
        getUserInfo();
        refreshPlants();
      } else {
        showErrorNotification(response.detail.error);
      }
    } catch (error) {
      showErrorNotification(t("notificationStrings.somethingWrong"));
    } finally {
      setShowOverlayLoader(false);
    }
  };
  const deleteHandler = async (params) => {
    handleUnshare(params.email);
  };

  const handleDialogOpen = () => {
    setEmail("");
    setComments("");
    setErrorMsg("");
    setDialogOpen(true);
    getUserInfo();
  };

  const handleCommentsChange = (value) => {
    setComments(value);
  };

  const handleChange = (value) => {
    setEmail(value);
    if (value.trim() === "") {
      setErrorMsg(t("sharePlant.emptyErrorMessage"));
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setErrorMsg([t("sharePlant.invalidEmail")]);
    } else {
      setErrorMsg("");
    }
  };

  const colDefs = [
    {
      headerName: t("manageAccess.index"),
      valueGetter: (params) => getIndex(params, params.data.email),
      minWidth: 80,
      flex: 0.25,
    },
    {
      headerName: t("manageAccess.email"),
      field: "email",
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: t("manageAccess.type"),
      field: "type",
      minWidth: 80,
      flex: 0.5,
    },
    {
      headerName: t("manageAccess.comments"),
      field: "comments",
      minWidth: 150,
      flex: 1,
    },
    {
      colId: "delete",
      lockPinned: true,
      pinned: "right",
      width: 80,
      headerName: t("ValueStrings.delete"),
      cellRenderer: DeleteRenderer,
      cellClass: "unshare-button",
    },
  ];

  return (
    <>
      {sharing.trim().toLowerCase() === "shared" ? (
        is_owner === true ? (
          <DupontButton
            type="tertiary"
            customClass="dupont-btn__tertiary--black  custom-sharing-button"
            onClick={(event) => {
              handleDialogOpen();
            }}
          >
            <u>{t("sharePlant.sharedWithOthers")}</u>
          </DupontButton>
        ) : (
          <span>{t("sharePlant.sharedByothers")}</span>
        )
      ) : (
        <span>{t("sharePlant.private")}</span>
      )}
      {dialogOpen && (
        <CustomModal
          header={t("manageAccess.title")}
          confirmBtn={t("ValueStrings.share")}
          cancelBtn={t("ValueStrings.cancel")}
          width="765px"
          confirmBtnDisabled={!email || errorMsg}
          onModalClose={onClose}
          isLoading={showOverlayLoader}
        >
          <div className="ro-ag-table" id="manage-access-table">
            <WPAgGrid
              columnDefs={colDefs}
              rowData={rowData}
              tableHeight={calculateTableHeight(rowData)}
              context={{
                deleteHandler,
              }}
            />
          </div>

          <div className="share marginTop10">
            {t("sharePlant.userMsg")} <span style={{ color: "red" }}>*</span>
          </div>
          <InputBox
            placeholder={t("sharePlant.enterEmail")}
            onChange={handleChange}
            maxLength={250}
          />

          <div
            style={{
              color: errorMsg ? "red" : "grey",
              textAlign: "left",
              marginTop: "10px",
            }}
          >
            {errorMsg || t("sharePlant.roUser")}
          </div>
          <div className="share marginTop10">{t("manageAccess.comments")}</div>
          <InputBox
            placeholder={t("manageAccess.comments")}
            rows={2}
            maxLength={225}
            onChange={handleCommentsChange}
          />
        </CustomModal>
      )}
    </>
  );
};
